import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useScrollTo } from 'hooks/use-scroll-to';

import { Icon, IconVariants } from 'core/generic/elements/icons';

import { LocaleSelector } from '../../templates/locale-selector';
import styles from './footer.module.scss';
import { BeeIcon, CrestIcon, CrownIcon, RoseIcon, ShipIcon } from './svg';

type FooterLink = {
	url: string;
	target?: string | null;
	name: string;
	rel?: string;
};

type SocialNetwork = {
	icon: string;
	id: string;
	link: FooterLink;
};

export type FooterData = {
	partnerLinks?: null;
	footerLinks: FooterLink[];
	socialLinks: SocialNetwork[];
	contactDetails: string;
	contactDetailsLink: FooterLink | null;
	copyright: string;
};

export type FooterProps = {
	data?: FooterData;
};

export function Footer({ data }: FooterProps) {
	const { scrollToTop } = useScrollTo();
	const { t, i18n } = useTranslation();
	const [languageModalOpen, setLanguageModalOpen] = useState(false);

	const socialIcons = new Map<string, IconVariants>([
		['#icon-youtube', 'BrandYoutubeFilled'],
		['#icon-whatsapp', 'BrandWhatsapp'],
		['#icon-instagram', 'BrandInstagram'],
		['#icon-x', 'BrandX'],
		['#icon-tiktok', 'BrandTikTok'],
		['#icon-facebook', 'BrandFacebook'],
		['#icon-linkedin', 'BrandLinkedin'],
		['#icon-onefootball', 'BrandOneFootball'],
	]);

	const hasSocialLinks = (data?.socialLinks?.length ?? 0) > 0;

	return (
		<footer className={styles['main-footer']} data-theme="dark">
			<div className={styles['nav-section']}>
				<div className={styles['city-logo-wrapper']}>
					<a
						href="https://www.mancity.com"
						className={styles['city-logo']}
						aria-label="Manchester City FC"
						title={t(
							'global.footer:copyrights',
							'Manchester City FC'
						)}
					>
						<Icon variant="CityCrest" />
					</a>
				</div>
				<button
					className={styles['back-to-top']}
					onClick={() => scrollToTop()}
					aria-label="Go to top button"
				>
					<span
						className={`body-xsmall bold ${styles['back-to-top-text']}`}
					>
						{t('global.footer:back_to_the_top', 'Go to top')}
					</span>
					<Icon variant="CircleArrowUp" />
				</button>
				<ul className={styles['primary-nav-list']}>
					{
						<li
							className={`${styles['nav-list-item']} ${
								!hasSocialLinks ? styles['no-social-links'] : ''
							}`}
						>
							<button
								className={styles['locale-selector']}
								aria-label="Locale Selector"
								onClick={() => setLanguageModalOpen(true)}
							>
								<Icon
									variant="Language"
									className={styles['language-icon']}
								/>
								<span className="body-xsmall bold">
									{i18n?.language || 'en'}
								</span>
							</button>
						</li>
					}
					{hasSocialLinks && (
						<li className={styles['nav-list-item']}>
							<ul className={styles['socials-list']}>
								{data?.socialLinks.map(
									(socialNetwork, index) => (
										<li
											key={socialNetwork.id}
											className={
												styles['socials-list-item']
											}
										>
											<a
												className={`${styles['socials-list-link']}`}
												href={socialNetwork.link.url}
												target={
													socialNetwork.link.target ||
													'_self'
												}
												rel={socialNetwork.link.rel}
												aria-label={
													socialNetwork.link.name
												}
												title={socialNetwork.link.name}
											>
												<Icon
													key={index}
													variant={
														socialIcons.get(
															socialNetwork.icon
														) || 'ShoppingBag'
													}
													className={`${
														styles['social-icons']
													} ${
														styles[
															'social-icons--' +
																socialNetwork.icon.replace(
																	'#icon-',
																	''
																)
														]
													}`}
												/>
											</a>
										</li>
									)
								)}
							</ul>
						</li>
					)}
				</ul>
				{data?.footerLinks && (
					<ul className={styles['secondary-nav-list']}>
						{data.footerLinks.map((list, index) => (
							<li key={index}>
								<a
									className={`body-small ${styles['secondary-nav-list-link']}`}
									href={list.url}
									target={list.target || '_self'}
								>
									{list.name[0].toUpperCase() +
										list.name.slice(1).toLowerCase()}
								</a>
							</li>
						))}
					</ul>
				)}
				<div></div>
			</div>
			<ul className={`body-small ${styles['info-list']}`}>
				<li className={styles['list-item']}>
					{data?.contactDetails ? (
						<a
							href={data?.contactDetailsLink?.url}
							target={data?.contactDetailsLink?.target || '_self'}
						>
							{data?.contactDetailsLink?.name}
						</a>
					) : (
						<>&nbsp;</>
					)}
				</li>
				<li className={styles['list-item']}>
					<ul
						className={styles['glyph-list']}
						aria-label="Glyph List"
					>
						<li className={`${styles['glyph-list-item']}`}>
							<RoseIcon />
						</li>
						<li className={`${styles['glyph-list-item']}`}>
							<CrestIcon />
						</li>
						<li className={`${styles['glyph-list-item']}`}>
							<ShipIcon />
						</li>
						<li className={`${styles['glyph-list-item']}`}>
							<BeeIcon />
						</li>
						<li className={`${styles['glyph-list-item']}`}>
							<CrownIcon />
						</li>
					</ul>
				</li>
				<li className={styles['list-item']}>
					{data?.copyright || <>&nbsp;</>}
				</li>
			</ul>
			{languageModalOpen && (
				<LocaleSelector
					visible={languageModalOpen}
					setVisible={setLanguageModalOpen}
				/>
			)}
		</footer>
	);
}

export default Footer;
